import React, { useEffect, useState } from 'react'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { Helmet } from 'react-helmet'
import { Link, useLocation } from 'react-router-dom'
import queryString from 'query-string'

import { AuthContainer } from '@/views/Auth/AuthContainer'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { apiService } from '@/api'
import { Banner } from '@/components/banners/Banner'
import { safeNextPath, saveSSOReturnPath, withNext } from '@/reducers/auth/loginNext.helpers'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const SSOLoginPageComponent = () => {
  const [error, setError] = useState()
  const query = useQuery()
  const { next } = queryString.parse(useLocation().search)
  useEffect(() => {
    const err = query.get('error')
    if (err) {
      setError(err)
    }
  }, [query])

  const handleLogin = async ({ domain }) => {
    try {
      const response = await fetch(`${apiService.auth}/sso/domain/cloud/${domain}`, {
        method: 'GET',
      })

      if (!response.ok) {
        throw new Error('Invalid domain or server error')
      }

      const ssoDomainResponse = await response.json()
      const { organization_uuid } = ssoDomainResponse

      // Two carriers, because neither covers every route back.
      //
      // RelayState is SAML's own mechanism: auth-service puts `next` there, the
      // identity provider returns it untouched, and auth-service appends it to
      // the redirect (`safe_next_path` in saml_sso.py). That survives a new tab
      // and a different browser profile.
      //
      // sessionStorage is the fallback for when nothing comes back -- an
      // IdP-initiated login supplies a RelayState we never set, so auth-service
      // has no `next` to return. currentUser.actions reads the query string
      // first and only then falls back to this copy.
      const nextPath = safeNextPath(next)
      const nextParam = nextPath ? `&next=${encodeURIComponent(nextPath)}` : ''

      saveSSOReturnPath(nextPath)
      window.location.href = `${apiService.auth}/cloud/sso/login?organization_uuid=${organization_uuid}${nextParam}`
    } catch (err) {
      console.error('SSO login failed:', err)
      setError(err.message || 'An unexpected error occurred')
    }
  }

  return (
    <>
      <Helmet>
        <title>Login | Balto Cloud</title>
      </Helmet>
      <AuthContainer>
        <AbstractForm
          schema={[{ name: 'domain', type: 'text', label: 'Domain', required: true }]}
          onSubmit={handleLogin}
          buttonProps={{ fullWidth: true }}
          buttonLabel="Login"
        />
        {error && (
          <Banner critical style={{ flex: 1 }}>
            <div style={{ textAlign: 'center' }}>{error}</div>
          </Banner>
        )}
        <Link to={withNext('/login', next)} className="link">
          Login using username and password
        </Link>
      </AuthContainer>
    </>
  )
}

export default withLDConsumer()(SSOLoginPageComponent)
